// src/modules/operaciones/inventarios/dashboard/MapeoColumnas.js
//
// Modal para corregir a mano qué columna cumple cada rol (ITEM, Descripción, Unidad, Fabricante...)
// cuando la detección automática falla. Los cambios se aplican todos juntos con "Aplicar".
//
// USO
//   const d = useDashboard(parseado);
//   const [mapeoAbierto, setMapeoAbierto] = useState(false);
//
//   <BotonMapeoColumnas hayMapeo={d.hayMapeo} onClick={() => setMapeoAbierto(true)} />
//   <MapeoColumnas dashboard={d} abierto={mapeoAbierto} onCerrar={() => setMapeoAbierto(false)} />
//
//   // o con props sueltas:
//   <MapeoColumnas roles={d.roles} columnas={columnas} mapeoManual={d.mapeoManual}
//                  setMapeo={d.setMapeo} restablecerMapeo={d.restablecerMapeo} hayMapeo={d.hayMapeo}
//                  abierto={...} onCerrar={...} />
//
// Cada rol tiene 3 posibilidades en el selector:
//   "Automático"  → vuelve a la detección (setMapeo(rol, undefined))
//   "Ninguna"     → deja el rol vacío     (setMapeo(rol, null))
//   una columna   → fuerza esa columna    (setMapeo(rol, "col3"))
//
// Notas:
//  - Los bloques de merma por bodega, los totales y la venta se detectan por sus encabezados
//    (MERMA DESCONOCIDA › GRAN COLOMBIA › $); no se corrigen aquí. Si fallan, hay que revisar
//    los encabezados del Excel.
//  - Al aplicar un mapeo, los filtros se reinician (el hook lo hace), porque los catálogos
//    de fabricantes, familias, etc. pueden cambiar.
//  - Si no pasas `columnas`, se toman de `dashboard.analisis.columnas`.

import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, RotateCcw, Settings2, X } from "lucide-react";

/* ========================================================================== */
/* Roles que se pueden corregir                                               */
/* ========================================================================== */

// `donde`: "raiz" → roles[clave]   |   "genericos" → roles.genericos[clave]
const ROLES_PRINCIPALES = [
  { clave: "itemCol", etiqueta: "Código (ITEM)", ayuda: "Identificador del artículo. No se suma.", donde: "raiz", importante: true },
  { clave: "descripcionCol", etiqueta: "Descripción", ayuda: "Nombre del artículo; de aquí sale la familia.", donde: "raiz" },
  { clave: "unidadCol", etiqueta: "Unidad de medida", ayuda: "UND, KG...", donde: "raiz" },
  { clave: "fabricanteCol", etiqueta: "Fabricante", ayuda: "Fabricante, marca o proveedor.", donde: "raiz" },
];

// Solo se muestran cuando el archivo NO es una matriz de mermas (análisis genérico)
const ROLES_GENERICOS = [
  { clave: "categoria", etiqueta: "Categoría / familia", donde: "genericos" },
  { clave: "bodega", etiqueta: "Bodega / sede", donde: "genericos" },
  { clave: "marca", etiqueta: "Marca", donde: "genericos" },
  { clave: "proveedor", etiqueta: "Proveedor", donde: "genericos" },
  { clave: "cantidad", etiqueta: "Cantidad", donde: "genericos" },
  { clave: "costo", etiqueta: "Costo", donde: "genericos" },
  { clave: "precio", etiqueta: "Precio", donde: "genericos" },
  { clave: "valor", etiqueta: "Valor / total", donde: "genericos" },
  { clave: "ganancia", etiqueta: "Ganancia", donde: "genericos" },
  { clave: "perdida", etiqueta: "Pérdida / merma", donde: "genericos" },
  { clave: "fecha", etiqueta: "Fecha", donde: "genericos" },
];

const AUTO = "__auto__";
const NINGUNA = "__ninguna__";

/* ========================================================================== */
/* Utilidades                                                                 */
/* ========================================================================== */

// 0 → "A", 25 → "Z", 26 → "AA" (letra de la columna en Excel)
function letraColumna(indice) {
  let n = indice + 1;
  let s = "";
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

const idActual = (roles, rol) => (rol.donde === "genericos" ? roles?.genericos?.[rol.clave] : roles?.[rol.clave]) || null;

// mapeoManual → valor del selector
function aValorSelector(mapeoManual, clave) {
  if (!Object.prototype.hasOwnProperty.call(mapeoManual || {}, clave)) return AUTO;
  const v = mapeoManual[clave];
  return v === null || v === "" ? NINGUNA : v;
}

// valor del selector → argumento de setMapeo
function aArgumentoMapeo(valor) {
  if (valor === AUTO) return undefined;
  if (valor === NINGUNA) return null;
  return valor;
}

function borradorDesde(mapeoManual) {
  const b = {};
  [...ROLES_PRINCIPALES, ...ROLES_GENERICOS].forEach((r) => {
    b[r.clave] = aValorSelector(mapeoManual, r.clave);
  });
  return b;
}

/* ========================================================================== */
/* Botón para abrir el modal                                                  */
/* ========================================================================== */

export function BotonMapeoColumnas({ onClick, hayMapeo = false, className = "" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
        hayMapeo
          ? "border-blue-300 bg-blue-50 text-blue-800"
          : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
      } ${className}`}
    >
      <Settings2 className="w-4 h-4" aria-hidden="true" />
      Columnas
      {hayMapeo && <span className="w-2 h-2 rounded-full bg-blue-600" title="Hay columnas asignadas manualmente" />}
    </button>
  );
}

/* ========================================================================== */
/* Modal                                                                      */
/* ========================================================================== */

export function MapeoColumnas({ dashboard = null, abierto = false, onCerrar = () => {}, ...propios }) {
  const p = { ...(dashboard || {}), ...propios };
  const roles = p.roles || null;
  const columnas = p.columnas || p.analisis?.columnas || [];
  const mapeoManual = p.mapeoManual || {};
  const { setMapeo, restablecerMapeo } = p;
  const hayMapeo = Boolean(p.hayMapeo);

  const [borrador, setBorrador] = useState(() => borradorDesde(mapeoManual));

  // Cada vez que se abre, el borrador parte del mapeo vigente
  useEffect(() => {
    if (abierto) setBorrador(borradorDesde(mapeoManual));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto]);

  // Esc para cerrar + bloqueo del scroll de la página
  useEffect(() => {
    if (!abierto) return undefined;
    const tecla = (e) => {
      if (e.key === "Escape") onCerrar();
    };
    const previo = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", tecla);
    return () => {
      document.body.style.overflow = previo;
      document.removeEventListener("keydown", tecla);
    };
  }, [abierto, onCerrar]);

  const esMermas = roles?.modo === "mermas" || roles?.esMatrizMermas;
  const listaRoles = esMermas ? ROLES_PRINCIPALES : [...ROLES_PRINCIPALES, ...ROLES_GENERICOS];

  const columnasPorId = useMemo(() => new Map(columnas.map((c) => [c.id, c])), [columnas]);
  const opcionesColumna = useMemo(
    () =>
      columnas.map((c, i) => ({
        id: c.id,
        texto: `${letraColumna(Number.isFinite(c.indice) ? c.indice : i)} · ${c.nombre}${c.tipo ? ` (${c.tipo})` : ""}`,
      })),
    [columnas]
  );

  // Cambios pendientes: roles cuyo valor del borrador difiere del mapeo vigente
  const cambios = useMemo(
    () => listaRoles.filter((r) => borrador[r.clave] !== aValorSelector(mapeoManual, r.clave)),
    [listaRoles, borrador, mapeoManual]
  );

  // Columna que quedará asignada a cada rol (null = ninguna, undefined = no se sabe)
  const asignacion = (rol) => {
    const v = borrador[rol.clave];
    if (v === NINGUNA) return null;
    if (v !== AUTO) return v;
    // Automático: solo se conoce si el rol no tiene hoy un mapeo manual
    if (Object.prototype.hasOwnProperty.call(mapeoManual, rol.clave)) return undefined;
    return idActual(roles, rol);
  };

  const sinItem = asignacion(ROLES_PRINCIPALES[0]) === null;

  // Columnas repetidas en dos roles
  const repetidas = useMemo(() => {
    const usos = new Map();
    listaRoles.forEach((r) => {
      const id = asignacion(r);
      if (id) usos.set(id, [...(usos.get(id) || []), r.etiqueta]);
    });
    return [...usos.entries()].filter(([, l]) => l.length > 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [borrador, roles, mapeoManual, listaRoles]);

  if (!abierto || typeof document === "undefined") return null;

  const aplicar = () => {
    // Las llamadas se agrupan en un solo render (el hook usa actualizaciones funcionales)
    cambios.forEach((r) => setMapeo(r.clave, aArgumentoMapeo(borrador[r.clave])));
    onCerrar();
  };

  const restablecer = () => {
    restablecerMapeo();
    setBorrador(borradorDesde({}));
    onCerrar();
  };

  const nombreDe = (id) => (id ? columnasPorId.get(id)?.nombre || id : null);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 p-3 sm:p-6"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCerrar();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="mapeo-titulo"
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col"
        style={{ maxHeight: "92vh" }}
      >
        {/* Encabezado */}
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-gray-100">
          <div>
            <h2 id="mapeo-titulo" className="text-base font-semibold text-gray-900">
              Asignación de columnas
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Corrige qué columna del archivo corresponde a cada dato si la detección automática se equivocó.
            </p>
          </div>
          <button
            type="button"
            onClick={onCerrar}
            aria-label="Cerrar"
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Cuerpo */}
        <div className="px-5 py-4 overflow-y-auto space-y-4">
          {sinItem && (
            <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
              <span>
                No hay una columna de <strong>ITEM</strong> asignada. El análisis seguirá funcionando, pero no
                se podrán detectar códigos repetidos ni mostrar el código en los rankings.
              </span>
            </div>
          )}

          {repetidas.length > 0 && (
            <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
              <span>
                La misma columna está asignada a más de un rol:{" "}
                {repetidas.map(([id, l]) => `${nombreDe(id)} → ${l.join(" y ")}`).join("; ")}.
              </span>
            </div>
          )}

          <div className="space-y-3">
            {listaRoles.map((rol) => {
              const actualId = idActual(roles, rol);
              const valor = borrador[rol.clave];
              return (
                <div key={rol.clave} className="grid grid-cols-1 sm:grid-cols-5 gap-1 sm:gap-3 sm:items-center">
                  <div className="sm:col-span-2">
                    <label htmlFor={`mapeo-${rol.clave}`} className="text-sm font-medium text-gray-800">
                      {rol.etiqueta}
                      {rol.importante && <span className="text-red-500"> *</span>}
                    </label>
                    <p className="text-xs text-gray-500 leading-snug">
                      {rol.ayuda ? `${rol.ayuda} ` : ""}
                      <span className="text-gray-400">
                        Ahora: {actualId ? nombreDe(actualId) : "sin asignar"}
                      </span>
                    </p>
                  </div>
                  <select
                    id={`mapeo-${rol.clave}`}
                    value={valor}
                    onChange={(e) => setBorrador((b) => ({ ...b, [rol.clave]: e.target.value }))}
                    className={`sm:col-span-3 w-full rounded-lg border px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      valor === AUTO ? "border-gray-200 text-gray-700" : "border-blue-300 text-blue-900 bg-blue-50"
                    }`}
                  >
                    <option value={AUTO}>Automático</option>
                    <option value={NINGUNA}>— Ninguna —</option>
                    {opcionesColumna.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.texto}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>

          {esMermas && (
            <p className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
              Los bloques de merma por bodega, los totales y la venta se detectan por los encabezados del
              archivo y no se editan aquí.
            </p>
          )}
        </div>

        {/* Pie */}
        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2 px-5 py-3 border-t border-gray-100 bg-gray-50 rounded-b-xl">
          <button
            type="button"
            onClick={restablecer}
            disabled={!hayMapeo}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <RotateCcw className="w-4 h-4" aria-hidden="true" />
            Restablecer
          </button>
          <div className="flex items-center gap-2 sm:justify-end">
            <button
              type="button"
              onClick={onCerrar}
              className="flex-1 sm:flex-none rounded-lg border border-gray-200 bg-white px-4 py-1.5 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={aplicar}
              disabled={cambios.length === 0}
              className="flex-1 sm:flex-none rounded-lg bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            >
              Aplicar{cambios.length > 0 ? ` (${cambios.length})` : ""}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default MapeoColumnas;